// Motion primitives (C11a motion, DES2). The keyframes and tokens live in ui/motion.css;
// this module times them, plays them only when they mean something, and honours
// reduced motion. Only transform and opacity move.
//
// Pure helpers (stagger, countUpText, flipDelta) run under node --test; the hooks need a DOM.
import {
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * Milliseconds, mirroring DES1's tokens in ui/tokens.css (--pc-dur-*) and this module's own
 * choreography constants in ui/motion.css (--pc-m-dur-*). test/ui-motion-unit.mjs keeps them equal.
 */
export const DUR = {
  instant: 80,
  quick: 160,
  standard: 240,
  gentle: 360,
  arrive: 560,
  ambient: 2600,
  count: 800,
  glow: 1600,
} as const;
export type Dur = keyof typeof DUR;

export const EASE = {
  enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
  cross: 'cubic-bezier(0.65, 0, 0.35, 1)',
} as const;

/** --pc-stagger and --pc-stagger-face (ui/tokens.css). */
export const STAGGER_MS = 40;
export const STAGGER_FACE_MS = 60;
/** After this many siblings the rest arrive together: a long list never crawls in. */
export const STAGGER_CAP = 12;

const REDUCE = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(REDUCE).matches
    : false;
}

/** The system's reduced-motion setting, live. (No Poneva switch: NOTES-v3 decision 22.) */
export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(prefersReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(REDUCE);
    const update = () => setReduce(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduce;
}

const entered = new Set<string>();

/**
 * True the first time `key` mounts in this page session, false afterwards. Motion plays on
 * entering a screen (or a new build, a new revision), not when a tab switch or an everyday
 * click remounts the same thing. Use a key that changes when the moment is new, e.g.
 * `reveal:${revisionId}`.
 */
export function useEnterOnce(key: string): boolean {
  // Decided once per key: a change of key (a new revision) is a new moment.
  const [state, setState] = useState(() => ({ key, first: !entered.has(key) }));
  const current = state.key === key ? state : { key, first: !entered.has(key) };
  if (current !== state) setState(current);
  useEffect(() => {
    entered.add(key);
  }, [key]);
  return current.first;
}

/** Test seam: forget which moments have played. */
export function resetEnteredMoments(): void {
  entered.clear();
}

/** The delay for sibling `index`, capped so siblings past the cap arrive with the last step. */
export function staggerDelay(
  index: number,
  opts: { step?: number; cap?: number; base?: number } = {},
): number {
  const step = opts.step ?? STAGGER_MS;
  const cap = opts.cap ?? STAGGER_CAP;
  const base = opts.base ?? 0;
  return base + Math.min(Math.max(0, Math.floor(index)), cap) * step;
}

/** Style for sibling `index` of a .pc-m-reveal / .pc-m-fade / .pc-m-lens sequence. */
export function stagger(
  index: number,
  opts?: { step?: number; cap?: number; base?: number },
): CSSProperties {
  return { ['--pc-m-delay' as string]: `${staggerDelay(index, opts)}ms` } as CSSProperties;
}

/** easeOutCubic: fast at first, settling on the exact value. */
const settle = (t: number) => 1 - (1 - t) ** 3;

/**
 * The text a count-up shows at `elapsed` ms. It ends on `final` exactly: the formatted
 * stored value, with its unit and locale, never a re-rounded number.
 */
export function countUpText(
  value: number,
  final: string,
  format: (n: number) => string,
  elapsed: number,
  duration: number = DUR.count,
): string {
  if (!Number.isFinite(value) || duration <= 0 || elapsed >= duration) return final;
  const t = Math.max(0, elapsed) / duration;
  return format(value * settle(t));
}

/**
 * A number that counts up once, from zero, the first time it is revealed. `final` is what
 * stays. With reduced motion, or when `play` is false, it shows `final` at once.
 */
export function CountUp({
  value,
  final,
  format,
  play = true,
  duration = DUR.count,
  delay = 0,
}: {
  value: number;
  final: string;
  format: (n: number) => string;
  play?: boolean;
  duration?: number;
  delay?: number;
}): ReactNode {
  const reduce = useReducedMotion();
  // Decided at mount: it counts once, the first time; a later value (a refresh) shows its
  // final text at once.
  const [counts] = useState(() => play && !reduce && Number.isFinite(value) && value !== 0);
  const counted = useRef(false);
  const [text, setText] = useState(() => (counts ? format(0) : final));
  useEffect(() => {
    // Reduced motion turned on mid-count stops the count and shows the final text.
    if (!counts || reduce || counted.current || typeof requestAnimationFrame !== 'function') {
      setText(final);
      return;
    }
    let frame = 0;
    let start: number | null = null;
    let finished = false;
    const tick = (now: number) => {
      start ??= now + delay;
      const next = countUpText(value, final, format, now - start, duration);
      setText(next);
      if (next !== final) frame = requestAnimationFrame(tick);
      else finished = counted.current = true;
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      // An interrupted count (StrictMode's double effect, a remount) may start again;
      // a finished one never does.
      if (!finished) setText(final);
    };
    // format is a formatter; a new function identity is not a new value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts, reduce, value, final, duration, delay]);
  // tabular figures keep the width still while the digits change
  return createElement(
    'span',
    { className: 'pc-m-count', style: { fontVariantNumeric: 'tabular-nums' } },
    text,
  );
}

/** Two layers in one grid cell; the shown one fades in as the other fades out. */
export function Crossfade({
  show,
  a,
  b,
  className,
}: {
  show: 'a' | 'b';
  a: ReactNode;
  b: ReactNode;
  className?: string;
}): ReactNode {
  return createElement(
    'div',
    { className: className ? `pc-m-crossfade ${className}` : 'pc-m-crossfade' },
    // The hidden layer is inert: out of the accessibility tree and out of the Tab order.
    createElement(
      'div',
      { 'data-shown': String(show === 'a'), 'aria-hidden': show !== 'a', inert: show !== 'a' },
      a,
    ),
    createElement(
      'div',
      { 'data-shown': String(show === 'b'), 'aria-hidden': show !== 'b', inert: show !== 'b' },
      b,
    ),
  );
}

type Box = { left: number; top: number; width: number; height: number };

/** The inverse transform that puts an element back where it was, for a FLIP move. */
export function flipDelta(first: Box, last: Box): { dx: number; dy: number } | null {
  const dx = first.left - last.left;
  const dy = first.top - last.top;
  return Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 ? null : { dx, dy };
}

/** A child's box relative to its container, so scrolling between updates moves nothing. */
const boxOf = (el: Element, container: Element): Box => {
  const r = el.getBoundingClientRect();
  const c = container.getBoundingClientRect();
  return { left: r.left - c.left, top: r.top - c.top, width: r.width, height: r.height };
};

/**
 * FLIP for a container's keyed children (`data-flip-key`): when `deps` change, children that
 * moved slide from their old place to the new one (transform only). New children don't move;
 * pair them with .pc-m-fade and a delay so they arrive after the move ("answers reshape").
 */
export function useFlip(container: RefObject<HTMLElement | null>, deps: readonly unknown[]): void {
  const before = useRef(new Map<string, Box>());
  const reduce = useReducedMotion();
  const snapshot = () => {
    const map = new Map<string, Box>();
    const root = container.current;
    root?.querySelectorAll<HTMLElement>('[data-flip-key]').forEach((el) => {
      map.set(el.dataset.flipKey!, boxOf(el, root));
    });
    return map;
  };
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;
    const previous = before.current;
    const now = snapshot();
    if (!reduce && previous.size) {
      el.querySelectorAll<HTMLElement>('[data-flip-key]').forEach((child) => {
        const old = previous.get(child.dataset.flipKey!);
        const next = now.get(child.dataset.flipKey!);
        const delta = old && next ? flipDelta(old, next) : null;
        if (!delta || typeof child.animate !== 'function') return;
        child.animate(
          [{ transform: `translate(${delta.dx}px, ${delta.dy}px)` }, { transform: 'none' }],
          { duration: DUR.arrive, easing: EASE.enter },
        );
      });
    }
    before.current = now;
    // deps are the caller's; the snapshot is ours
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
